// Shared helpers for the inbound receiving appointment ops surfaces (board,
// unload recording, extemporáneo registration, capacity overrides).
// API contract: platform docs/inbound-appointments-api-contract.md (PR #4091).

import { dateInTimezone } from './warehouseDates'

export const ACTIVE_APPOINTMENT_STATUSES = ['booked', 'checked_in']
export const EXTEMPORANEOUS_ALLOWED_STATUSES = ['WITHOUT_SCHEDULING', 'REQUESTED', 'RECEIVED', 'PROCESSING']

// Board "Llegadas" view: rows that represent expected/actual trucks for the
// day. canceled/superseded are history — they must not inflate counters.
// no_show stays in deliberately: a truck that failed to arrive is part of the
// day's operational picture (spec §1).
export const ARRIVAL_APPOINTMENT_STATUSES = ['booked', 'checked_in', 'no_show']
export const isArrivalAppointment = appointment => ARRIVAL_APPOINTMENT_STATUSES.includes(appointment?.status)

// Checked in on an earlier warehouse-local day than booked: the check-in
// already freed the booked day's capacity, so the row is not that day's load.
// arrived_on_time can't tell (early is always on time); no timezone → not early.
export const isEarlyArrival = (appointment, timeZone) => {
  if (appointment?.status !== 'checked_in' || !/^\d{4}-\d{2}-\d{2}$/.test(appointment.date)) return false
  const arrivalDate = dateInTimezone(appointment.checked_in_at, timeZone)
  return !!arrivalDate && arrivalDate < appointment.date
}

// The one partition the day board and the week grid share.
export const splitEarlyArrivals = (appointments = [], timeZone) => {
  const onDay = []
  const early = []
  appointments.forEach(appointment => (isEarlyArrival(appointment, timeZone) ? early : onDay).push(appointment))
  early.sort((a, b) => Date.parse(a.checked_in_at) - Date.parse(b.checked_in_at))
  return { onDay, early }
}

// The replenishment serializer keeps the nested appointment while it is
// booked or checked_in; terminal states come back as null.
export const hasActiveAppointment = replenishment =>
  !!replenishment?.inbound_appointment && ACTIVE_APPOINTMENT_STATUSES.includes(replenishment.inbound_appointment.status)

// Extemporáneo creation is the one ops write gated by the warehouse `enabled`
// flag (422 inbound_scheduling_disabled), and is allowed through PROCESSING —
// paperwork often follows receiving.
export const canRegisterExtemporaneous = replenishment =>
  !!replenishment &&
  replenishment.inbound_scheduling_enabled === true &&
  !hasActiveAppointment(replenishment) &&
  EXTEMPORANEOUS_ALLOWED_STATUSES.includes(replenishment.status)

// Appointment endpoints use the nested {error: {code}} envelope; the
// replenishment-update endpoints use the flat {error, code} one. Tolerate both.
export const getAppointmentErrorCode = error => {
  const data = error?.response?.data
  if (!data) return null
  if (data.error && typeof data.error === 'object') return data.error.code || null
  return data.code || null
}

// Codes are an open enum — unknown codes fall back to the generic message.
export const translateAppointmentError = (i18n, error) => {
  const generic = i18n.t('replenishments.inbound_appointments.errors.generic')
  const code = getAppointmentErrorCode(error)
  if (!code) return generic
  return i18n.t(`replenishments.inbound_appointments.errors.${code}`, { defaultValue: generic })
}

// Feeds the shared <StatusPill statuses={...}> component
// (src/components/BasicTable/StatusPill.js) — the first entry is the fallback
// used when a status is missing from the list.
export const appointmentStatusPillDefs = i18n => [
  { status: 'unknown', label: i18n.t('replenishments.inbound_appointments.statuses.unknown'), classes: 'border-gray-300 text-gray-400' },
  { status: 'booked', label: i18n.t('replenishments.inbound_appointments.statuses.booked'), classes: 'border-blue-500 text-blue-500' },
  { status: 'checked_in', label: i18n.t('replenishments.inbound_appointments.statuses.checked_in'), classes: 'border-green-600 text-green-600' },
  { status: 'canceled', label: i18n.t('replenishments.inbound_appointments.statuses.canceled'), classes: 'border-red-400 text-red-400' },
  { status: 'no_show', label: i18n.t('replenishments.inbound_appointments.statuses.no_show'), classes: 'border-red-600 text-red-600' },
  { status: 'superseded', label: i18n.t('replenishments.inbound_appointments.statuses.superseded'), classes: 'border-gray-400 text-gray-400' },
]

// Board rows grouped by franja in start-time order. The group list is the
// union of the warehouse catalog and any franja nested on an appointment
// (defensive: the serializer nests the full franja object). Empty catalog
// groups are KEPT — an ops board that shows free windows is the point.
// Extemporáneos (null franja) form the final group, only when non-empty.
export const groupAppointmentsByFranja = (appointments = [], franjas = []) => {
  const byId = new Map()
  franjas.forEach(franja => byId.set(franja.id, { franja, appointments: [] }))

  const extemporaneous = []
  appointments.forEach(appointment => {
    if (!appointment.franja) {
      extemporaneous.push(appointment)
      return
    }
    if (!byId.has(appointment.franja.id)) byId.set(appointment.franja.id, { franja: appointment.franja, appointments: [] })
    byId.get(appointment.franja.id).appointments.push(appointment)
  })

  const groups = Array.from(byId.values()).sort((a, b) => (a.franja.starts_at || '').localeCompare(b.franja.starts_at || ''))
  if (extemporaneous.length > 0) groups.push({ franja: null, appointments: extemporaneous })
  return groups
}

const OVERRIDE_LEVERS = ['dock_count_override', 'man_hours_override', 'units_delta']

// Overrides are append-only in v1. The backend's resolution (platform
// capacity.rb#resolve_dock_capacity) is FIELD-level and BLOCKED-STICKY:
//
//   any blocked row for the scope  ──▶ capacity 0, no matter what is newer
//   newest row CARRYING a lever    ──▶ wins that lever for its scope
//   reason-only rows               ──▶ never resolve anything
//
// So a row is "superseded" (safe to dim) only when every lever it carries
// has a newer same-scope row carrying that same lever — and blocked rows
// are never dimmed.
//
// "Newest" MUST match the backend, which resolves with `created_at DESC, id DESC`
// (capacity.rb's overrides_for): created_at is authoritative and id only breaks exact
// ties. Ranking by id alone diverges whenever sequence allocation and commit order
// disagree — ordinary under concurrent creates, and guaranteed for a row backdated by
// a console fix. Falls back to id when either timestamp is absent or unparseable.
export const isNewerOverride = (row, other) => {
  if (other == null) return true
  const a = Date.parse(row?.created_at)
  const b = Date.parse(other?.created_at)
  if (!Number.isNaN(a) && !Number.isNaN(b) && a !== b) return a > b
  return row.id > other.id
}

export const markSupersededOverrides = (overrides = []) => {
  const newestLeverRow = new Map()
  overrides.forEach(row => {
    OVERRIDE_LEVERS.forEach(lever => {
      if (row[lever] == null) return
      const key = `${lever}|${row.date}|${row.receiving_franja_id ?? 'day'}`
      if (isNewerOverride(row, newestLeverRow.get(key) ?? null)) newestLeverRow.set(key, row)
    })
  })
  return overrides.map(row => {
    if (row.blocked) return { ...row, isSuperseded: false }
    const levers = OVERRIDE_LEVERS.filter(lever => row[lever] != null)
    if (levers.length === 0) return { ...row, isSuperseded: false }
    const isSuperseded = levers.every(lever => newestLeverRow.get(`${lever}|${row.date}|${row.receiving_franja_id ?? 'day'}`)?.id !== row.id)
    return { ...row, isSuperseded }
  })
}

// Capacity-silence posture — the ONE owner (eng-review D2). 404 = the
// summary route isn't deployed (feature absent); 403 = a grant problem the
// appointments calls surface loudly anyway. Both are silent for capacity.
// TODOS.md item 2(b) sunsets the 404 tolerance post-#4229: flip it HERE and
// both the day board and the week fan-out follow.
export const isCapacitySummaryAbsent = error => {
  const status = error?.response?.status
  return status === 404 || status === 403
}

// Each day is split with splitEarlyArrivals first: early rows feed earlyCounts only.
// Week-overview grid model (spec 2026-08-18 week-overview §4). The row set is
// derived through groupAppointmentsByFranja over the window's on-day rows, so
// the union rule (catalog + appointment-nested franjas, start-time order,
// empty catalog rows kept) has one owner and provably matches the day board.
// Cell counts use Llegadas semantics (isArrivalAppointment); null-franja
// arrivals feed the per-day extemporaneous counts. `summary` rides through raw
// so the shared strip renders it.
export const buildWeekOverview = (days = [], catalogFranjas = [], timeZone) => {
  const splitDays = days.map(day => ({ day, ...splitEarlyArrivals(day.appointments || [], timeZone) }))
  // filter(Boolean): groupAppointmentsByFranja's trailing extemporaneous
  // group has franja: null — extemporáneos get their own row below.
  const franjas = groupAppointmentsByFranja(
    splitDays.flatMap(({ onDay }) => onDay),
    catalogFranjas,
  )
    .map(group => group.franja)
    .filter(Boolean)

  const franjaRows = franjas.map(franja => ({
    franja,
    cells: splitDays.map(({ day, onDay }) => ({
      date: day.date,
      arrivals: onDay.filter(a => a.franja?.id === franja.id && isArrivalAppointment(a)).length,
      summary: (day.summary || []).find(s => s.receiving_franja_id === franja.id) || null,
    })),
  }))

  const extemporaneousCounts = splitDays.map(({ onDay }) => onDay.filter(a => !a.franja && isArrivalAppointment(a)).length)
  const earlyCounts = splitDays.map(({ early }) => early.length)
  const totals = days.map((day, index) => franjaRows.reduce((sum, row) => sum + row.cells[index].arrivals, 0) + extemporaneousCounts[index])

  return {
    dates: days.map(day => day.date),
    franjaRows,
    extemporaneousCounts,
    hasExtemporaneous: extemporaneousCounts.some(count => count > 0),
    earlyCounts,
    hasEarly: earlyCounts.some(count => count > 0),
    totals,
    capacityDegraded: days.some(day => !!day.summaryDegraded),
  }
}
